import type { UseFormReturn } from 'react-hook-form';

import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Field, Input } from '~/components/Form';

type FormWithCoEntryNumber = {
  coEntryNumber: string;
};

type Props<T extends FormWithCoEntryNumber> = {
  form: T extends FormWithCoEntryNumber ? UseFormReturn<T> : never;
  previousEntryNumber: string | null;
};

export const FormCoEntryNumber = <T extends FormWithCoEntryNumber>({ form: { control }, previousEntryNumber }: Props<T>) => {
  const { t } = useTranslation(['quotations', 'validation']);

  return (
    <Controller
      control={control}
      name="coEntryNumber"
      render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
        <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
          <Field.Label>{t('quotations:fields.entryNumber.label')}</Field.Label>

          <Input
            onBlur={onBlur}
            onValueChange={onChange}
            placeholder={t('quotations:fields.entryNumber.placeholder')}
            ref={ref}
            value={value}
          />

          <Field.Footer>
            {previousEntryNumber !== null && (
              <Field.Description>
                <Trans
                  components={[<strong key={null} />]}
                  i18nKey="quotations:fields.entryNumber.last"
                  values={{ entryNumber: previousEntryNumber }}
                />
              </Field.Description>
            )}

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Footer>
        </Field.Root>
      )}
      rules={{
        maxLength: {
          value: 255,
          message: t('validation:maxLength', { attribute: t('quotations:fields.entryNumber.label'), max: 255 }),
        },
        required: { value: true, message: t('validation:required') },
        validate: (value) => !!value.trim() || t('validation:required'),
      }}
    />
  );
};
